import { Component, HostListener, Injector, afterNextRender, effect, inject, signal } from '@angular/core';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { AuthService } from './auth.service';
import { CatalogService } from './catalog.service';

const THEME_STORAGE_KEY = 'chevron-ai-plugins-theme';

@Component({
  selector: 'app-root',
  imports: [RouterLink, RouterOutlet],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App {
  protected readonly auth = inject(AuthService);
  private readonly catalog = inject(CatalogService);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);

  protected readonly isDarkMode = signal(App.readStoredTheme());
  protected readonly showUserMenu = signal(false);
  protected readonly showBackToTop = signal(false);

  constructor() {
    afterNextRender(() => this.measureTopBarHeight());

    // Load the real catalog once signed in; drop it on sign-out so a new user never sees
    // a previous session's cached plugin list. Kept here (rather than in the catalog page)
    // so it survives regardless of which route is active when auth state changes.
    effect(() => {
      if (this.auth.isAuthenticated()) {
        void this.loadCatalogThenSearch();
      } else {
        this.catalog.reset();
      }
    });
  }

  /**
   * The shallow catalog drives first paint. Queue the larger GitHub-backed corpus only after
   * Angular has committed the rendered grid, so the extra request cannot delay the marketplace.
   */
  private async loadCatalogThenSearch(): Promise<void> {
    await this.catalog.load();
    afterNextRender(() => {
      if (this.auth.isAuthenticated()) {
        void this.catalog.loadSearchIndex();
      }
    }, { injector: this.injector });
  }

  private static readStoredTheme(): boolean {
    return localStorage.getItem(THEME_STORAGE_KEY) === 'dark';
  }

  protected toggleTheme(): void {
    this.isDarkMode.update((value) => !value);
    localStorage.setItem(THEME_STORAGE_KEY, this.isDarkMode() ? 'dark' : 'light');
  }

  protected toggleUserMenu(): void {
    this.showUserMenu.update((value) => !value);
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.showUserMenu()) {
      return;
    }
    const insideMenu = (event.target as HTMLElement).closest('.user-menu');
    if (!insideMenu) {
      this.showUserMenu.set(false);
    }
  }

  protected async logout(): Promise<void> {
    this.showUserMenu.set(false);
    await this.auth.logout();
    await this.router.navigateByUrl('/login');
  }

  @HostListener('window:scroll')
  protected onWindowScroll(): void {
    this.showBackToTop.set(window.scrollY > 300);
  }

  @HostListener('window:resize')
  protected onWindowResize(): void {
    this.measureTopBarHeight();
  }

  private measureTopBarHeight(): void {
    const header = document.querySelector('.top-bar');
    if (header instanceof HTMLElement) {
      document.documentElement.style.setProperty('--top-bar-height', `${header.offsetHeight}px`);
    }
  }

  protected scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
